import { useEffect, useReducer, useRef, useState } from 'react'
import { css } from '@firebolt-dev/css'
import { cls } from '../utils'

const s = n => `calc(${n}px * var(--ui-scale, 1))`

export function GameFrames({ world }) {
  const frames = world.frames
  const [items, setItems] = useState(() => [...frames.items.values()])

  useEffect(() => {
    setItems([...frames.items.values()])
    return frames.subscribe(() => setItems([...frames.items.values()]))
  }, [frames])

  return (
    <div
      className='frames'
      css={css`
        position: absolute;
        inset: 0;
        z-index: 5;
        pointer-events: none;
        overflow: hidden;
      `}
    >
      {items.map(item => (
        <Frame key={item.id} item={item} />
      ))}
    </div>
  )
}

// ── item wrapper — holds the DOM ref, bypasses React for position ────────────
// Anchor point (wx/wy projected to screen) is the bottom-center of the widget.

function Frame({ item }) {
  const ref = useRef()
  const [, rerender] = useReducer(n => n + 1, 0)

  useEffect(() => {
    item.el = ref.current
    item._rerender = rerender
    return () => {
      item.el = null
      item._rerender = null
    }
  }, [])

  return (
    <div
      ref={ref}
      css={css`
        position: absolute;
        top: 0%;
        left: 0;
        > div {
          transform: translateX(-50%) translateY(-100%);
        }
      `}
    >
      <div>
        {item.type === 'unit' && <FrameUnit {...item.props} />}
        {item.type === 'bubble' && <FrameBubble {...item.props} />}
        {item.type === 'interact' && <FrameInteract {...item.props} />}
      </div>
    </div>
  )
}

function FrameInteract() {
  return (
    <div
      css={css`
        width: ${s(5 * 3)};
        height: ${s(4 * 3)};
        background: url('/assets/interact-chevron.png');
        image-rendering: pixelated;
        background-size: cover;
        animation: interact-hover 1s ease-in-out infinite;
        @keyframes interact-hover {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(${s(-4)});
          }
        }
      `}
    />
  )
}

function FrameBubble({ text }) {
  return (
    <div
      css={css`
        position: relative;
        background: rgba(0, 0, 0, 0.8);
        border-radius: ${s(8)};
        padding: ${s(9)} ${s(12)};
        max-width: ${s(200)};
        font-size: ${s(16)};
        color: white;
        /* white-space: pre-wrap; */
        /* word-break: break-word; */
        line-height: 1.1;
      `}
    >
      {text}
    </div>
  )
}

function FrameUnit({ name, health, maxHealth, energy, showEnergy, hidden }) {
  if (hidden) return null
  const healthPerc = maxHealth > 0 ? Math.max(0, Math.min(1, health / maxHealth)) : 0
  const energyPerc = energy > 0 ? Math.max(0, Math.min(1, energy / 100)) : 0
  const healthPct = `${healthPerc * 100}%`
  const energyPct = `${energyPerc * 100}%`
  return (
    <div
      className='unit'
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${s(1)};
        padding: 0 0 ${s(2)};
        .unit-name {
          font-size: ${s(16)};
          line-height: 1;
          color: white;
          white-space: nowrap;
          -webkit-font-smoothing: none;
          -webkit-text-stroke: ${s(5)} black;
          paint-order: stroke fill;
        }
        .unit-health {
          width: ${s(16 * 3)};
          height: ${s(4 * 3)};
          background: url('/assets/healthbar.png');
          image-rendering: pixelated;
          background-size: cover;
          padding: ${s(1 * 3)};
          position: relative;
          &-shade {
            position: absolute;
            top: ${s(1 * 3)};
            left: ${s(1 * 3)};
            bottom: ${s(1 * 3)};
            width: ${s(1 * 3)};
            background: rgba(0, 0, 0, 0.2);
          }
          &-inner {
            height: 100%;
            width: ${healthPct};
            background: #e22c4d;
          }
          &.hidden {
            opacity: 0;
          }
        }
        .unit-energy {
          width: ${s(16 * 3)};
          height: ${s(3 * 3)};
          background: url('/assets/energybar.png');
          image-rendering: pixelated;
          background-size: cover;
          padding: ${s(1 * 3)};
          position: relative;
          &-shade {
            position: absolute;
            top: ${s(1 * 3)};
            left: ${s(1 * 3)};
            bottom: ${s(1 * 3)};
            width: ${s(1 * 3)};
            background: rgba(0, 0, 0, 0.2);
          }
          &-inner {
            height: ${s(1 * 3)};
            width: ${energyPct};
            background: #e05f1f;
            transition: width 0.1s linear;
          }
          &.hidden {
            opacity: 0;
          }
        }
      `}
    >
      <div className='unit-name'>{name}</div>
      <div className={cls('unit-health', { hidden: health === maxHealth })}>
        <div className='unit-health-inner' />
        <div className='unit-health-shade' />
      </div>
      <div className={cls('unit-energy', { hidden: !showEnergy || energy >= 100 })}>
        <div className='unit-energy-inner' />
        <div className='unit-energy-shade' />
      </div>
    </div>
  )
}
